/**
 * Catalog-driven editor for a set of device posture requirements (VSNP-1174).
 *
 * Each row is attribute × operator × value, where the attribute comes from the
 * posture catalog (typed: enum/bool render a select, version/string a text
 * input) and the operator list is filtered by the attribute's value type. Used
 * by the Posture Profile modal; the policy modal only picks a profile by name.
 *
 * Controlled: the parent owns the rows. Helpers convert between the editable
 * rows and the API's `Record<key, PosturePredicate>` shape.
 *
 * Each row says which platforms measure its fact and warns when some platform
 * can never meet it (VSNP-1957). Keys not offered in this release are only
 * listed for a row that already uses them, so editing never drops them.
 */

import { Plus, X } from 'lucide-react';
import type { PostureOp } from '../../services/access.service';
import {
  PLATFORM_LABEL,
  POSTURE_CATALOG,
  opsForType,
  platformList,
  platformSummary,
  requirementNotes,
  requirementShortfalls,
  unknownAttribute,
  valueLabel,
  type PostureAttribute,
} from '../../data/postureCatalog';
import {
  MULTI_OPS,
  catalogAttribute,
  defaultValueFor,
  fromPostureRows,
  nextPostureRow,
  type PostureRow,
} from '../../utils/postureRows';

/** Human label + comparison symbol for each operator. */
const OP_LABEL: Record<PostureOp, string> = {
  eq: 'is', neq: 'is not', in: 'in', not_in: 'not in', gte: '≥', lte: '≤', gt: '>', lt: '<',
};

/** The catalog attribute for a row's key, falling back to a synthetic one so a
 *  key not in the catalog (legacy data) still renders and is never dropped. */
function attrForRow(key: string): PostureAttribute {
  return catalogAttribute(key) ?? unknownAttribute(key);
}

/** "Measured on iOS, Android and Linux (CLI) · macOS: unknown · Windows: no Secure Access app yet". */
function platformLine(attr: PostureAttribute): string | null {
  if (attr.origin === 'gateway') return 'Checked by the gateway on every session, not reported by the device.';
  if (!attr.reports) return null;
  const { measured, fixed, notAvailable } = platformSummary(attr);
  const parts: string[] = [];
  if (measured.length > 0) parts.push(`Measured on ${platformList(measured)}`);
  for (const f of fixed) parts.push(`${PLATFORM_LABEL[f.platform]}: always ${f.value}`);
  if (notAvailable.length > 0) parts.push(`${platformList(notAvailable)}: no Secure Access app yet`);
  return parts.join(' · ');
}

interface PostureRequirementsEditorProps {
  rows: PostureRow[];
  onChange: (rows: PostureRow[]) => void;
  /** Prefix for element ids (datalists), unique per mounted editor. */
  idPrefix?: string;
}

const PostureRequirementsEditor = ({ rows, onChange, idPrefix = 'pr' }: PostureRequirementsEditorProps) => {
  const add = () => onChange([...rows, nextPostureRow(rows)]);
  const update = (i: number, patch: Partial<PostureRow>) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  // Changing the attribute resets the op + value to that attribute's defaults,
  // so an enum never keeps an operator or value that doesn't fit its type.
  const changeKey = (i: number, key: string) => {
    const attr = attrForRow(key);
    update(i, { key, op: opsForType(attr.valueType)[0], value: defaultValueFor(attr) });
  };
  const remove = (i: number) => onChange(rows.filter((_, idx) => idx !== i));

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium text-gray-700">Requirements</span>
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
        >
          <Plus className="w-3.5 h-3.5" /> Add requirement
        </button>
      </div>

      {rows.length === 0 ? (
        <p className="text-xs text-gray-500">No requirements yet — add at least one verified device fact.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((row, i) => {
            const attr = attrForRow(row.key);
            const ops = opsForType(attr.valueType);
            const isMulti = MULTI_OPS.has(row.op);
            const inCatalog = POSTURE_CATALOG.some((a) => a.key === row.key);
            const hiddenKey = !inCatalog && !!catalogAttribute(row.key);
            const useSelectValue = (attr.valueType === 'enum' || attr.valueType === 'bool') && !isMulti;
            const strayValue = useSelectValue && !!row.value && !(attr.allowedValues ?? []).includes(row.value);
            const listId = `${idPrefix}-values-${i}`;
            const pred = fromPostureRows([row])[row.key.trim()];
            const shortfalls = pred === undefined ? [] : requirementShortfalls(row.key, pred);
            const notes = pred === undefined ? [] : requirementNotes(row.key, pred);
            const platforms = platformLine(attr);
            return (
              <div key={i} className="flex flex-col gap-0.5">
                <div className="flex items-center gap-2">
                  <select
                    className="input-base flex-1"
                    value={row.key}
                    onChange={(e) => changeKey(i, e.target.value)}
                    title={attr.description}
                  >
                    {!inCatalog && row.key && (
                      <option value={row.key}>{hiddenKey ? `${attr.label} (not in this release)` : `${row.key} (custom)`}</option>
                    )}
                    {POSTURE_CATALOG.map((a) => (
                      <option key={a.key} value={a.key}>
                        {a.label}
                      </option>
                    ))}
                  </select>
                  <select
                    className="input-base w-24 shrink-0"
                    value={row.op}
                    onChange={(e) => update(i, { op: e.target.value as PostureOp })}
                    title="Comparison"
                  >
                    {ops.map((op) => (
                      <option key={op} value={op}>
                        {OP_LABEL[op]}
                      </option>
                    ))}
                  </select>
                  {useSelectValue ? (
                    <select
                      className="input-base w-40"
                      value={row.value}
                      onChange={(e) => update(i, { value: e.target.value })}
                    >
                      {strayValue && <option value={row.value}>{row.value} (no client reports this)</option>}
                      {(attr.allowedValues ?? []).map((v) => (
                        <option key={v} value={v}>
                          {valueLabel(attr, v)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className="input-base w-40"
                      list={attr.allowedValues ? listId : undefined}
                      placeholder={isMulti ? 'a, b' : attr.valueType === 'version' ? '10.15' : 'value'}
                      value={row.value}
                      onChange={(e) => update(i, { value: e.target.value })}
                    />
                  )}
                  {attr.allowedValues && !useSelectValue && (
                    <datalist id={listId}>
                      {attr.allowedValues.map((v) => (
                        <option key={v} value={v} />
                      ))}
                    </datalist>
                  )}
                  <button
                    type="button"
                    onClick={() => remove(i)}
                    className="p-1 text-gray-400 hover:text-danger-600"
                    title="Remove requirement"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                {platforms && (
                  <p className="text-[11px] pl-1 text-gray-500" data-testid="posture-row-platforms">
                    {platforms}
                  </p>
                )}
                {(attr.note || !attr.measured) && !hiddenKey && (
                  <p className={`text-[11px] pl-1 ${attr.measured ? 'text-gray-400' : 'text-amber-600'}`}>
                    {!attr.measured && '⚠ not measured yet — '}
                    {attr.note}
                  </p>
                )}
                {(shortfalls.length > 0 || notes.length > 0) && (
                  <ul className="text-[11px] pl-1 text-amber-700 space-y-0.5" data-testid="posture-row-warnings">
                    {shortfalls.map((s) => (
                      <li key={s.platform}>⚠ {s.message}</li>
                    ))}
                    {notes.map((n) => (
                      <li key={n}>⚠ {n}</li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
          {rows.some((r) => MULTI_OPS.has(r.op)) && (
            <p className="text-xs text-gray-400">
              For <span className="font-medium">in / not in</span>, separate values with commas
              (e.g. <code>attested, mdm</code>).
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default PostureRequirementsEditor;
